import DateUtil from "@/flit2go-web-core/util/dateUtil";

export const SUCCESS_REDIRECT_TYPE = "f2g:successRedirect";
export const SUCCESS_REDIRECT_VERSION = 1;
export const DEFAULT_TIME_SEC = 5;
// setTimeout fires immediately when the delay exceeds a signed 32-bit int (~24.8 days).
export const MAX_TIMEOUT_MS = 2147483647;

const URL_PARAM = "?successRedirectUrl=";
const TIME_PARAM = "?successRedirectTime=";

const RECURRENCE_MAP = { WEK: "WEE", MON: "MON" };

const isEmpty = (value) => value === null || value === undefined || value === "";

const toStr = (value) => (isEmpty(value) ? "" : String(value));

export const isValidHttpUrl = (str) => {
  if (typeof str !== "string" || str === "") {
    return false;
  }
  try {
    const parsed = new URL(str);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch (e) {
    return false;
  }
};

const readParam = (href, param) =>
  href
    .split(param)
    .pop()
    .split("#")[0];

const isTopWindow = () => {
  try {
    return window.parent === window;
  } catch (e) {
    return false;
  }
};

// Same "?key=" convention as the rest of the frame attributes parsed in App.vue.
// Ignored when the frame is the top window: the url would come from a link anyone can craft (open redirect).
export const parseSuccessRedirectAttrs = (href) => {
  if (
    typeof href !== "string" ||
    !href.includes(URL_PARAM) ||
    isTopWindow()
  ) {
    return null;
  }
  let url;
  try {
    url = decodeURIComponent(readParam(href, URL_PARAM));
  } catch (e) {
    return null;
  }
  if (!isValidHttpUrl(url)) {
    return null;
  }
  let timeSec = DEFAULT_TIME_SEC;
  if (href.includes(TIME_PARAM)) {
    const rawTime = readParam(href, TIME_PARAM);
    if (/^\d+$/.test(rawTime)) {
      timeSec = parseInt(rawTime, 10);
    }
  }
  return { url, timeSec };
};

export const toTimeoutMs = (timeSec) => Math.min(timeSec * 1000, MAX_TIMEOUT_MS);

// Local wall-clock time in the same timezone the end screen uses to render dates.
export const formatLocalIsoMinute = (dateOrMs) => {
  if (isEmpty(dateOrMs)) {
    return "";
  }
  const date = dateOrMs instanceof Date ? dateOrMs : Number(dateOrMs);
  if (Number.isNaN(date instanceof Date ? date.getTime() : date)) {
    return "";
  }
  return DateUtil.formatCustomDate(date, undefined, "YYYY-MM-DDTHH:mm");
};

export const formatAmount = (n) => {
  if (isEmpty(n) || Number.isNaN(Number(n))) {
    return "";
  }
  return Number(n).toFixed(2);
};

export const buildRentalParams = ({
  bookingId,
  amount,
  currency,
  category,
  startDate,
  endDate,
  startBranch,
  endBranch,
} = {}) => ({
  bookingId: toStr(bookingId),
  amount: formatAmount(amount),
  currency: toStr(currency),
  category: toStr(category),
  startDate: formatLocalIsoMinute(startDate),
  endDate: formatLocalIsoMinute(endDate),
  startBranch: toStr(startBranch),
  endBranch: toStr(endBranch),
});

export const buildSubscriptionParams = ({
  bookingId,
  amount,
  currency,
  recurrence,
  category,
  startDate,
  startBranch,
} = {}) => ({
  bookingId: toStr(bookingId),
  amount: formatAmount(amount),
  currency: toStr(currency),
  recurrence: RECURRENCE_MAP[recurrence] || "",
  category: toStr(category),
  startDate: formatLocalIsoMinute(startDate),
  startBranch: toStr(startBranch),
});

// KEEP IN SYNC with flit2go-iframe-js/iframeFlit2go.min.js (buildRedirectUrl)
// RFC 3986: encodeURIComponent leaves !'()* unescaped; URLSearchParams is not used because it encodes spaces as "+".
export const encodeRfc3986 = (value) =>
  encodeURIComponent(value).replace(
    /[!'()*]/g,
    (c) => "%" + c.charCodeAt(0).toString(16).toUpperCase()
  );

export const isPlainObject = (value) =>
  value !== null &&
  typeof value === "object" &&
  Object.prototype.toString.call(value) === "[object Object]";

// KEEP IN SYNC with flit2go-iframe-js/iframeFlit2go.min.js (buildRedirectUrl)
export const buildRedirectUrl = (url, params) => {
  const hashIndex = url.indexOf("#");
  const base = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : url.slice(hashIndex);

  const pairs = [];
  if (isPlainObject(params)) {
    Object.keys(params).forEach((key) => {
      if (typeof params[key] === "string") {
        pairs.push(encodeRfc3986(key) + "=" + encodeRfc3986(params[key]));
      }
    });
  }
  if (pairs.length === 0) {
    return url;
  }

  let separator = "&";
  if (base.indexOf("?") === -1) {
    separator = "?";
  } else if (/[?&]$/.test(base)) {
    separator = "";
  }
  return base + separator + pairs.join("&") + hash;
};

// Returns true when the parent page is notified (it performs the redirect), false when there is no parent page.
// The frame never navigates itself (open redirect).
export const emitSuccessRedirect = (product, url, params) => {
  if (isTopWindow()) {
    return false;
  }
  window.parent.postMessage(
    {
      type: SUCCESS_REDIRECT_TYPE,
      version: SUCCESS_REDIRECT_VERSION,
      product,
      url,
      params,
    },
    "*"
  );
  return true;
};

export default {
  SUCCESS_REDIRECT_TYPE,
  SUCCESS_REDIRECT_VERSION,
  DEFAULT_TIME_SEC,
  MAX_TIMEOUT_MS,
  isValidHttpUrl,
  parseSuccessRedirectAttrs,
  toTimeoutMs,
  formatLocalIsoMinute,
  formatAmount,
  buildRentalParams,
  buildSubscriptionParams,
  encodeRfc3986,
  isPlainObject,
  buildRedirectUrl,
  emitSuccessRedirect,
};
